import { Component, OnInit, inject } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';

import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/select';
import { MatError } from '@angular/material/input';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AssemblyTaskModel } from 'src/app/models/assembly-task.model';
import { ItemCategoryModel } from 'src/app/models/item-category.model';
import { SapArticleModel } from 'src/app/models/sap-article.model';
import { AssemblyOrderService } from 'src/app/services/assembly-order.service';
import { SapArticleService } from 'src/app/services/sap-article.service';
import { ToastService } from 'src/app/services/toast.service';
import { ManufacturingItemEnum } from 'src/app/shared/enumerators/manufacturingItemEnum.enum';
import { TwoDigitDecimaNumberDirective } from 'src/app/directives/TwoDigitDecimaNumberDirective';

export interface AssemblyTaskModalData {
  assemblyOrderId: string;
  task?: AssemblyTaskModel;
  existingInternalCodes: string[];
}

@Component({
  selector: 'app-assembly-task-modal',
  templateUrl: './assembly-task-modal.component.html',
  styleUrls: ['./assembly-task-modal.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatError,
    TwoDigitDecimaNumberDirective
],
})
export class AssemblyTaskModalComponent implements OnInit {
  private readonly assemblySvc = inject(AssemblyOrderService);
  private readonly sapArticleSvc = inject(SapArticleService);
  private readonly dialogRef = inject(MatDialogRef<AssemblyTaskModalComponent>);
  private readonly data = inject<AssemblyTaskModalData>(MAT_DIALOG_DATA);
  toastr = inject(ToastService);

  assemblyOrderId = this.data.assemblyOrderId;
  task = this.data.task;

  taskForm!: UntypedFormGroup;
  itemCategories: ItemCategoryModel[] = [];
  isSubmitting = false;
  submitted = false;

  allArticles: SapArticleModel[] = [];
  filteredArticles: SapArticleModel[] = [];
  searchText = '';
  selectedItem: SapArticleModel | null = null;

  manufacturingItemTypes = Object.entries(ManufacturingItemEnum)
    .filter(([, value]) => typeof value === 'number')
    .map(([label, value]) => ({ label, value: value as ManufacturingItemEnum }));

  // Solo se puede agregar/editar ítems de estos tipos en una OA.
  private readonly allowedTypes: ManufacturingItemEnum[] = [
    ManufacturingItemEnum.Armado,
    ManufacturingItemEnum.AccesorioLagune,
    ManufacturingItemEnum.Equipamiento,
    ManufacturingItemEnum.Deposito,
    ManufacturingItemEnum.Revestimiento,
    ManufacturingItemEnum.Orientacion,
    ManufacturingItemEnum.AccesorioSpa,
  ];

  get isEditMode(): boolean {
    return !!this.task;
  }

  ngOnInit(): void {
    this.taskForm = this.getForm();

    this.assemblySvc.getItemCategories().subscribe((categories) => {
      this.itemCategories = categories;
    });

    this.sapArticleSvc.getAllArticles().subscribe((articles) => {
      this.allArticles = articles.filter(
        (a) =>
          this.allowedTypes.includes(a.type) &&
          !this.data.existingInternalCodes.includes(a.internalCode)
      );
      if (this.task) {
        const match = articles.find(
          (a) => a.internalCode === this.task!.internalCode
        );
        if (match) {
          this.selectItem(match);
        }
      }
    });
  }

  getForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      quantity: new UntypedFormControl(
        this.task?.quantity ?? null,
        Validators.required
      ),
    });
  }

  // Bloquea caracteres inválidos a medida que se escribe, en vez de dejar
  // escribir cualquier cosa y recién avisar con un error al final: solo
  // dígitos, un único punto decimal, hasta 2 decimales, y tope de 99 en la
  // parte entera.
  onQuantityInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9.]/g, '');

    const firstDot = value.indexOf('.');
    if (firstDot !== -1) {
      value =
        value.slice(0, firstDot + 1) +
        value.slice(firstDot + 1).replace(/\./g, '');
    }

    let [intPart, decPart] = value.split('.');

    if (intPart.length > 2) {
      intPart = intPart.slice(0, 2);
    }
    if (intPart && Number(intPart) > 99) {
      intPart = '99';
    }
    if (decPart !== undefined) {
      decPart = decPart.slice(0, 2);
    }

    value = decPart !== undefined ? `${intPart}.${decPart}` : intPart;

    input.value = value;
    this.taskForm.get('quantity')?.setValue(value, { emitEvent: false });
  }

  onSearchChange(value: string): void {
    this.searchText = value;
    this.selectedItem = null;
    const term = value.trim().toLowerCase();
    this.filteredArticles =
      term.length === 0
        ? []
        : this.allArticles
            .filter(
              (a) =>
                a.internalCode?.toLowerCase().includes(term) ||
                a.name?.toLowerCase().includes(term)
            )
            .slice(0, 50);
  }

  selectItem(article: SapArticleModel): void {
    this.selectedItem = article;
    this.searchText = `${article.internalCode} - ${article.name}`;
    this.filteredArticles = [];
  }

  getItemCategoryName(id?: string | null): string {
    return (
      this.itemCategories.find((c) => c.id?.toString() === id?.toString())
        ?.displayName ?? ''
    );
  }

  getTypeLabel(type?: ManufacturingItemEnum): string {
    return this.manufacturingItemTypes.find((t) => t.value === type)?.label ?? '';
  }

  onSubmit() {
    this.submitted = true;
    this.taskForm.markAllAsTouched();

    if (this.taskForm.invalid || !this.selectedItem || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    // El backend resuelve descripción/alias/categoría/tipo a partir del
    // internalCode; el frontend solo manda código y cantidad.
    const payload = {
      internalCode: this.selectedItem.internalCode,
      quantity: Number(this.taskForm.value.quantity),
    };

    if (this.isEditMode && this.task) {
      this.assemblySvc.updateAssemblyTask(this.task.id.toString(), payload).subscribe({
        next: (result) => {
          this.toastr.success(
            'Ítem actualizado',
            'El ítem se actualizó correctamente.'
          );
          this.dialogRef.close(result);
          this.isSubmitting = false;
        },
        error: (err) => {
          this.toastr.error(
            'Error',
            err?.error?.error ?? err?.error ?? 'No se pudo actualizar el ítem.'
          );
          this.isSubmitting = false;
        },
      });
    } else {
      const createPayload = {
        ...payload,
        assemblyOrderGroupId: this.assemblyOrderId,
      };
      this.assemblySvc.addAssemblyTask(createPayload).subscribe({
        next: (result) => {
          this.toastr.success(
            'Ítem agregado',
            'El ítem se agregó correctamente a la OA.'
          );
          this.dialogRef.close(result);
          this.isSubmitting = false;
        },
        error: (err) => {
          this.toastr.error(
            'Error',
            err?.error?.error ?? err?.error ?? 'No se pudo agregar el ítem.'
          );
          this.isSubmitting = false;
        },
      });
    }
  }

  closeClick() {
    this.dialogRef.close();
  }
}
